// src/components/MessageBubble.tsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  styled,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  Person,
  Assistant,
  VolumeUp,
  VolumeOff,
  BookmarkBorder,
  Bookmark,
  Close,
} from "@mui/icons-material";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import {
  Message,
  ContentPart,
  getMessageText,
  getMessageImages,
  getMessageAudio,
} from "../types";
import { AudioPlayer } from "./AudioPlayer";
import { InteractiveContent } from "./InteractiveContent";
import { LengthAdjustmentChips } from "./LengthAdjustmentChips";
import { useT } from '../i18n/useT';
import { writeFeedItem } from '../feed/writeFeedItem';
import { AnnotatedImage } from './feed/AnnotatedImage';
import {
  CollectionRevealCard,
  CollectionCompletionCard,
} from './collections/CollectionRevealCard';

const shouldForwardIsUserProp = (prop: PropertyKey) => prop !== "isUser";

const MessageContainer = styled(Box, {
  shouldForwardProp: shouldForwardIsUserProp,
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  display: "flex",
  flexDirection: isUser ? "row-reverse" : "row",
  alignItems: "flex-start",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.25),
  maxWidth: "100%",
}));

const StyledMessageBubble = styled(Paper, {
  shouldForwardProp: shouldForwardIsUserProp,
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  padding: theme.spacing(1.5, 2),
  maxWidth: "92%",
  backgroundColor: isUser
    ? theme.palette.primary.main
    : theme.palette.background.paper,
  color: isUser
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  borderRadius: theme.spacing(2.5),
  borderTopLeftRadius: isUser ? theme.spacing(2.5) : theme.spacing(1),
  borderTopRightRadius: isUser ? theme.spacing(1) : theme.spacing(2.5),
  boxShadow: isUser
    ? "0 8px 18px rgba(51, 102, 255, 0.18)"
    : "0 6px 14px rgba(0,0,0,0.08)",
  position: "relative",
  wordBreak: "break-word",
  whiteSpace: "pre-wrap",
  lineHeight: 1.5,
  letterSpacing: "0.01em",
}));

const MessageAvatar = styled(Avatar, {
  shouldForwardProp: shouldForwardIsUserProp,
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  backgroundColor: isUser
    ? theme.palette.primary.main
    : theme.palette.secondary.main,
  width: 32,
  height: 32,
}));

const ImagePreview = styled("img")({
  maxWidth: "100%",
  height: "auto",
  borderRadius: 8,
  marginTop: 8,
  maxHeight: 300,
  objectFit: "contain",
});

interface MessageBubbleProps {
  message: Message;
  onInteractiveClick?: (message: string, element: any) => void;
  onFollowUpClick?: (prompt: string) => void;
  recentMessages?: Message[];
  /** Forwarded to InteractiveContent so PlacesResultCard can ground
   *  AI-suggested places against nearby Google Places (used for
   *  distance + result ranking). */
  userLocation?: { lat: number; lng: number };
  /** Active conversation id. Phase 3.7.4 — required for the
   *  thumbs-up / saved_message path so feed_items get
   *  source.conversation_id populated; without it, the
   *  onMessageBookmark Cloud Function rejects with
   *  missing_source_refs and no curator memory is written.
   *  Falls back to message.conversation_id (if the message doc
   *  carries it as a denormalized field) for backwards
   *  compatibility. */
  conversationId?: string;
}

// Image renderer for chat messages. Storage paths AND URLs both
// flow through here. AnnotatedImage handles:
//   • URL passthrough (legacy data, external images) — no
//     annotation lookup, just renders.
//   • Storage path → resolves a download URL via the existing
//     useResolvedImageUrl cache, AND subscribes to the photo's
//     annotation doc. Once annotations land (a few seconds after
//     upload), tap-able dots fade in over the image.
//
// Annotations are produced by the annotatePhotoOnUpload Cloud
// Function — it fires only on user-camera uploads under the
// `images/{uid}/...` convention, so Places photos and other
// non-user content never trigger Gemini and never get dots.
const FirebaseImage: React.FC<{
  imagePath: string;
  alt?: string;
  onImageClick?: () => void;
}> = ({ imagePath, alt, onImageClick }) => {
  const t = useT();
  return (
    <AnnotatedImage
      source={imagePath}
      alt={alt || t('Attached image')}
      maxHeight={300}
      objectFit="contain"
      borderRadius={8}
      marginTop={8}
      onImageClick={onImageClick}
    />
  );
};

const createImageDataUrl = (imageData: string): string => {
  // Check if it's already a data URL
  if (imageData.startsWith("data:")) {
    return imageData;
  }

  // If it's base64 without the data URL prefix, add it
  if (imageData.match(/^[A-Za-z0-9+/].*={0,2}$/)) {
    return `data:image/jpeg;base64,${imageData}`;
  }

  // If it looks like a URL path, convert to absolute URL
  if (imageData.startsWith("/")) {
    return `${window.location.origin}${imageData}`;
  }

  // Return as-is for other cases (full URLs)
  return imageData;
};

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  onInteractiveClick,
  onFollowUpClick,
  recentMessages = [],
  userLocation,
  conversationId: conversationIdProp,
}) => {
  const t = useT();
  const isUser = message.role === "user";

  // Save-to-feed state. Pinning an AI message creates a feed_item
  // with kind='saved_message' and the AI's reply text snapshotted in
  // body. Default visibility is 'private' (the user's own pin /
  // bookmark), per HARDCODED_FEED_DEFAULTS in feed/types.ts.
  //
  // Local state tracks whether THIS session has already saved the
  // message; cross-session, message.metadata?.feed_item_id (set
  // server-side or here on save) prevents duplicates. We don't have
  // that field today, so duplicates ARE possible if the user reloads
  // and re-taps. Acceptable for v1; phase 2 can add the metadata.
  // Save-to-feed state.
  //
  // Persistence: cross-session "already saved" lives on the message
  // doc itself at metadata.feed_item_id. This is set when the user
  // bookmarks (along with the create) and cleared when they unbookmark
  // (along with the delete). Reading it from the prop means no extra
  // queries on render and the filled/outline icon reflects the true
  // state immediately.
  //
  // Toggle: tapping the icon when already saved DELETES the feed_item
  // and clears the metadata stamp, mirroring how heart/thumbs-up work
  // on guide cards. So "bookmark again" actually unbookmarks rather
  // than spawning a duplicate.
  //
  // Image attachment: walk backward through recentMessages from this
  // message and grab the first prior message that has any image
  // parts. Common case: user takes a photo, AI describes it, user
  // bookmarks the description — the photo is on the immediately
  // preceding user message. We walk all the way back, not just one
  // step, so multi-turn conversations about a photo still attach the
  // right image.
  const existingFeedItemId =
    (message.metadata as any)?.feed_item_id as string | undefined;
  const [savedFeedItemId, setSavedFeedItemId] = useState<string | undefined>(
    existingFeedItemId
  );
  const [saving, setSaving] = useState(false);
  const isSaved = !!savedFeedItemId;

  // Lightbox for tappable chat images. We track the source (storage
  // path or URL) of whichever image was tapped so the modal can show
  // exactly that one, regardless of how many images are in the
  // message. The modal renders once at the bottom of the bubble; null
  // = closed, anything else = open with that source. Reusing the same
  // dialog for all images per bubble keeps the DOM small even on
  // messages that attach a grid of photos.
  const [lightboxSource, setLightboxSource] = useState<string | null>(null);

  const findImageRef = (): string | undefined => {
    // Prefer the AI message's own images (rare).
    if (imageParts.length > 0 && imageParts[0]?.url) {
      return imageParts[0].url;
    }
    if (recentMessages.length === 0) return undefined;

    // Locate this message in the list; walk strictly backward from
    // its index. (recentMessages={messages} from
    // VirtualizedMessageList includes the entire conversation, so
    // unfiltered iteration could pick up later messages.)
    const myIdx = recentMessages.findIndex((m) => m?.id === message.id);
    const startFrom = myIdx >= 0 ? myIdx - 1 : recentMessages.length - 1;
    for (let i = startFrom; i >= 0; i--) {
      const prior = recentMessages[i];
      if (!prior) continue;
      const priorImages = getMessageImages(prior);
      if (priorImages.length > 0 && priorImages[0]?.url) {
        return priorImages[0].url;
      }
    }
    return undefined;
  };

  const handleSaveToFeed = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Phase 3.7.4 — prefer the conversationId passed as a prop
      // (always set from ChatInterface). Fall back to a denormalized
      // field on the message doc if present. Without conversationId
      // populated on the feed_item.source, the onMessageBookmark
      // server trigger silently rejects (missing_source_refs) and
      // no message_bookmark curator candidate is enqueued.
      const conversationId =
        conversationIdProp || (message as any).conversation_id;
      const messageRef = conversationId
        ? doc(db, 'conversations', conversationId, 'messages', message.id)
        : null;

      if (isSaved && savedFeedItemId) {
        // Untoggle — delete the feed_item, clear the metadata stamp.
        try {
          await deleteDoc(doc(db, 'feed_items', savedFeedItemId));
        } catch (err) {
          console.warn('handleSaveToFeed: delete feed_item failed:', err);
        }
        if (messageRef) {
          try {
            await updateDoc(messageRef, { 'metadata.feed_item_id': null });
          } catch (err) {
            console.warn('handleSaveToFeed: clear stamp failed:', err);
          }
        }
        setSavedFeedItemId(undefined);
        return;
      }

      // Create — capture image from this message or any prior one.
      const imageRef = findImageRef();
      const snapshot: Record<string, any> = {
        body: textContent,
        title: t('Saved from chat'),
      };
      if (imageRef) {
        snapshot.image_path = imageRef;
        snapshot.preview_url = imageRef;
      }
      const newId = await writeFeedItem({
        kind: 'saved_message',
        // Visibility deliberately omitted — resolveVisibility() pulls
        // the user's default for saved_message (private).
        snapshot,
        source: {
          kind: 'message',
          id: message.id,
          conversation_id: conversationId,
        },
      });
      if (newId) {
        setSavedFeedItemId(newId);
        if (messageRef) {
          try {
            await updateDoc(messageRef, { 'metadata.feed_item_id': newId });
          } catch (err) {
            console.warn('handleSaveToFeed: stamp message failed:', err);
          }
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Extract content from message parts
  const textContent = getMessageText(message);
  const imageParts = getMessageImages(message);
  const audioParts = getMessageAudio(message);

  const renderImages = () => {
    if (imageParts.length === 0) return null;

    return (
      <Box sx={{ mt: 1 }}>
        {imageParts.map((imagePart, index) => {
          // Check if this looks like a Firebase Storage path
          const isStoragePath =
            imagePart.url &&
            !imagePart.url.startsWith("http") &&
            !imagePart.url.startsWith("data:") &&
            imagePart.url.includes("/");

          if (isStoragePath) {
            return (
              <FirebaseImage
                key={index}
                imagePath={imagePart.url}
                alt={imagePart.alt || "Attached image"}
                onImageClick={() => setLightboxSource(imagePart.url)}
              />
            );
          } else {
            // Legacy handling for base64 or direct URLs
            try {
              const imageSrc = createImageDataUrl(imagePart.url);

              return (
                <ImagePreview
                  key={index}
                  src={imageSrc}
                  alt={imagePart.alt || "Attached image"}
                  loading="lazy"
                  onClick={() => setLightboxSource(imageSrc)}
                  style={{ cursor: 'zoom-in' }}
                  onError={(e) => {
                    console.error("Image failed to load:", e);
                    // Hide the image if it fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                  }}
                />
              );
            } catch (error) {
              console.error("Error processing image URL:", error);
              return (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    textAlign: "center",
                    mt: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    
                    {t("Image could not be displayed")}
                  </Typography>
                </Box>
              );
            }
          }
        })}
      </Box>
    );
  };

  // Audio-spinner UX guard. Three-state machine, derived from
  // (audioParts presence) × (audioGenerationFailed flag) ×
  // (time since the message was created):
  //
  //   audioParts.length > 0  → render the player
  //   audioGenerationFailed  → render a "tap to retry" /
  //                            "audio unavailable" affordance
  //   no audio + no fail flag + < 90s old → spinner ("Generating
  //                            audio narration...")
  //   no audio + no fail flag + > 90s old → assume server-side
  //                            silent failure (function killed
  //                            mid-flight, network died, etc.);
  //                            render the same "unavailable"
  //                            affordance as the explicit fail
  //                            case.
  //
  // The 90s ceiling matches the silence watchdog in
  // useStreamingChat. Real audio generation finishes in
  // 5–20s; anything beyond a minute and a half is dead.
  const AUDIO_GENERATION_TIMEOUT_MS = 90 * 1000;
  const messageAgeMs = useMemo(() => {
    const created = (message as any).created_at;
    if (!created) return 0;
    if (created instanceof Date) return Date.now() - created.getTime();
    if (typeof created?.toDate === 'function') {
      return Date.now() - (created.toDate() as Date).getTime();
    }
    if (typeof created === 'number') return Date.now() - created;
    return 0;
  }, [message]);

  // Live-tick once at the timeout boundary so a spinner that's
  // currently mid-window flips on its own. Without this, a user
  // sitting on a stuck spinner wouldn't see it resolve until
  // they triggered some other re-render. One timer per bubble,
  // cleaned up on unmount.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (audioParts.length > 0) return;
    if ((message as any).audioGenerationFailed) return;
    const remaining = AUDIO_GENERATION_TIMEOUT_MS - messageAgeMs;
    if (remaining <= 0) return;
    const t = setTimeout(() => forceTick((n) => n + 1), remaining + 100);
    return () => clearTimeout(t);
  }, [audioParts.length, message, messageAgeMs]);

  const audioFailed =
    !!(message as any).audioGenerationFailed ||
    (audioParts.length === 0 &&
      messageAgeMs > AUDIO_GENERATION_TIMEOUT_MS);

  const renderAudio = () => {
    // Show audio player as soon as audio parts are available
    if (audioParts.length > 0) {
      return (
        <Box sx={{ mt: 1 }}>
          {audioParts.map((audioPart, index) => (
            <AudioPlayer
              key={index}
              audioUrl={audioPart.url}
              title={
                audioPart.transcript
                  ? `Audio: ${audioPart.transcript}`
                  : "Tour guide narration"
              }
              compact={false}
            />
          ))}
        </Box>
      );
    }

    // Failure / timed-out state. Distinguishable affordance — no
    // spinner — so users don't sit forever waiting for audio
    // that's never coming. Tooltip surfaces the error reason on
    // hover for debugging.
    if (!isUser && audioFailed) {
      return (
        <Box sx={{ mt: 1 }}>
          <Chip
            icon={<VolumeOff sx={{ fontSize: 16 }} />}
            label={t("Audio unavailable")}
            size="small"
            variant="outlined"
            title={(message as any).audioFailureReason || undefined}
            sx={{
              borderColor: "text.disabled",
              color: "text.secondary",
              "& .MuiChip-icon": {
                color: "text.disabled",
              },
            }}
          />
        </Box>
      );
    }

    // In-flight state — generation kicked off recently and we
    // haven't seen a result yet. Cap matches the silence
    // watchdog so the spinner can't outlive the request.
    if (!isUser) {
      return (
        <Box sx={{ mt: 1 }}>
          <Chip
            icon={<CircularProgress size={16} />}
            label={t("Generating audio narration...")}
            size="small"
            variant="outlined"
            sx={{
              borderColor: "primary.main",
              color: "primary.main",
              "& .MuiChip-icon": {
                color: "primary.main",
              },
            }}
          />
        </Box>
      );
    }

    return null;
  };

  // ── Collection reveal / completion system messages ──
  //
  // The server writes these as role='system' messages with a
  // structured `collection_reveal` or `collection_completion` part.
  // We intercept BEFORE the standard bubble path so they render as
  // a dedicated full-width card — no avatar, no bubble chrome, no
  // bookmark button. They're discovery moments, not chat turns.
  if (message.role === 'system' && Array.isArray(message.parts)) {
    const revealPart = message.parts.find(
      (p: any) => p?.type === 'collection_reveal'
    ) as any;
    if (revealPart?.data) {
      return (
        <CollectionRevealCard
          data={revealPart.data}
          onFollowUpClick={onFollowUpClick}
        />
      );
    }
    const completionPart = message.parts.find(
      (p: any) => p?.type === 'collection_completion'
    ) as any;
    if (completionPart?.data) {
      return <CollectionCompletionCard data={completionPart.data} />;
    }
  }

  return (
    <>
    <MessageContainer isUser={isUser}>
      <MessageAvatar isUser={isUser}>
        {isUser ? <Person fontSize="small" /> : <Assistant fontSize="small" />}
      </MessageAvatar>

      <Box>
        <StyledMessageBubble isUser={isUser} elevation={1}>
          {textContent && (
            <InteractiveContent
              content={textContent}
              onInteractiveClick={onInteractiveClick}
              onFollowUpClick={onFollowUpClick}
              recentMessages={recentMessages}
              isUser={isUser}
              userLocation={userLocation}
            />
          )}

          {renderImages()}
          {renderAudio()}
        </StyledMessageBubble>

        {/* Tell-me-more / tell-me-less chips — only on assistant
            messages, only when we have BOTH the prose AND the
            preceding user prompt to anchor a re-roll against. The
            chips route through onFollowUpClick — same channel the
            existing follow-up question pills use — so the AI sees
            the request as a normal user message. */}
        {!isUser && textContent && onFollowUpClick && (() => {
          if (recentMessages.length === 0) return null;
          const myIdx = recentMessages.findIndex((m) => m?.id === message.id);
          const startFrom = myIdx >= 0 ? myIdx - 1 : recentMessages.length - 1;
          let prevUserPrompt = '';
          for (let i = startFrom; i >= 0; i--) {
            const prior = recentMessages[i];
            if (prior?.role !== 'user') continue;
            // Pull plain text out of either parts[] or legacy content.
            if (Array.isArray(prior.parts)) {
              for (const p of prior.parts) {
                if ((p as any)?.type === 'text' && typeof (p as any).content === 'string') {
                  prevUserPrompt += (p as any).content + ' ';
                }
              }
            } else if (typeof (prior as any).content === 'string') {
              prevUserPrompt = (prior as any).content;
            }
            prevUserPrompt = prevUserPrompt.trim();
            if (prevUserPrompt) break;
          }
          if (!prevUserPrompt) return null;
          return (
            <LengthAdjustmentChips
              previousUserPrompt={prevUserPrompt}
              previousAssistantText={textContent}
              onFollowUpClick={onFollowUpClick}
            />
          );
        })()}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            justifyContent: isUser ? 'flex-end' : 'flex-start',
            mt: 0.5,
            mx: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp(message.created_at)}
            {message.metadata?.model_version && !isUser && (
              <Typography
                component="span"
                variant="caption"
                color="text.disabled"
                sx={{ ml: 1 }}
              >
                • {message.metadata.model_version}
              </Typography>
            )}
          </Typography>
          {/* Save-to-feed button on assistant messages only.
              Pinning a message means "this is part of my experience,
              I want to come back to it." Saved items show up in the
              Feed tab as kind='saved_message' with private visibility
              by default. */}
          {!isUser && textContent && (
            <Tooltip
              title={
                isSaved
                  ? t('Saved — tap to remove')
                  : t('Save to feed')
              }
            >
              <IconButton
                size="small"
                onClick={handleSaveToFeed}
                disabled={saving}
                sx={{
                  p: 0.25,
                  color: isSaved ? 'primary.main' : 'text.disabled',
                }}
                aria-label={isSaved ? t('Saved to feed') : t('Save to feed')}
              >
                {isSaved ? (
                  <Bookmark sx={{ fontSize: 16 }} />
                ) : (
                  <BookmarkBorder sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </MessageContainer>

    {/* Image lightbox — opens when the user taps any image in the
        bubble. Mirrors the FeedItemCard lightbox pattern: dim
        backdrop, click-anywhere-to-close, top-right close button.
        Mounted once per bubble; the active source is whichever
        image was tapped most recently. */}
    <Dialog
      open={lightboxSource !== null}
      onClose={() => setLightboxSource(null)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0,0,0,0.92)',
          boxShadow: 'none',
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: '95vh' },
        },
      }}
    >
      <DialogContent
        onClick={() => setLightboxSource(null)}
        sx={{
          p: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          cursor: 'zoom-out',
          minHeight: { xs: '100vh', sm: 'auto' },
        }}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setLightboxSource(null);
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'common.white',
            bgcolor: 'rgba(0,0,0,0.4)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
            zIndex: 1,
          }}
          aria-label={t('Close')}
        >
          <Close />
        </IconButton>
        {lightboxSource !== null && (
          // AnnotatedImage handles both storage paths and URLs (data
          // URLs, http(s)) via its source resolver. mode="lightbox"
          // sizes to intrinsic dimensions and keeps annotation dots
          // tappable in the modal too — same behavior as the feed
          // card lightbox.
          <AnnotatedImage
            source={lightboxSource}
            alt=""
            mode="lightbox"
            onImageClick={() => setLightboxSource(null)}
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};
